import { zodResolver } from '@hookform/resolvers/zod'
import { GeoJsonObject } from 'geojson'
import { useForm, UseFormProps } from 'react-hook-form'
import { z } from 'zod'

import { makeGenericZodErrorMessage } from '@/utils/make-generic-zod-error-message'

import { useValidationInputs } from './use-validation-inputs'

export const perimeterSchema = z.object({
  input: z
    .array(
      z.instanceof(File),
      makeGenericZodErrorMessage('O perímetro é obrigatório!'),
    )
    .refine((list) => !!list.length, {
      message: 'O perímetro é obrigatório!',
    }),
  results: z.object(
    {
      filename: z.string(
        makeGenericZodErrorMessage(
          'Ocorreu um erro ao identificar o nome do perímetro',
        ),
      ),
      area: z
        .number(
          makeGenericZodErrorMessage(
            'Ocorreu um erro ao calcular a area de interesse',
          ),
        )
        .nonnegative(),
      lat_long: z.tuple([z.number(), z.number()]),
      geojson: z.custom<GeoJsonObject>((geojson) => {
        const { success } = z
          .object({
            features: z.array(z.any()),
          })
          .safeParse(geojson)

        return success
      }, 'Aconteceu algo de errado com a leitura do perímetro'),
    },
    makeGenericZodErrorMessage('Os resultados do perímetro são obrigatórios.'),
  ),
})

export const mosaicSchema = z
  .object({
    input: z
      .array(
        z.instanceof(File),
        makeGenericZodErrorMessage('O ortomosaico é obrigatório!'),
      )
      .refine((list) => !!list.length, {
        message: 'O ortomosaico é obrigatório!',
      }),
    results: z.object(
      {
        gsd: z.number(
          makeGenericZodErrorMessage('Ocorreu um erro ao identificar o gsd'),
        ),
        coord_x: z.number(
          makeGenericZodErrorMessage(
            'Ocorreu um erro ao identificar as coordenadas x do ortomosaico',
          ),
        ),
        coord_y: z.number(
          makeGenericZodErrorMessage(
            'Ocorreu um erro ao identificar as coordenadas y do ortomosaico',
          ),
        ),
        px_res: z.number(
          makeGenericZodErrorMessage(
            'Ocorreu um erro ao identificar as coordenadas do ortomosaico',
          ),
        ),
      },
      makeGenericZodErrorMessage(
        'Os resultados do ortomosaico são obrigatórios.',
      ),
    ),
  })
  .optional()

const geoTagSchema = z
  .object({
    input: z
      .array(
        z.instanceof(File),
        makeGenericZodErrorMessage('O geotag é obrigatório!'),
      )
      .refine((list) => !!list.length, {
        message: 'A geotag é obrigatória!',
      }),
  })
  .optional()

const jpgSchema = z
  .object({
    input: z
      .array(
        z.instanceof(File),
        makeGenericZodErrorMessage('As imagens são obrigatórias!'),
      )
      .refine((list) => !!list.length, {
        message: 'As imagens são obrigatórias!',
      })
      .refine((list) => list.length <= 10000, {
        message: 'Você excedeu o limite de 10.000 imagens.',
      }),
    results: z.object(
      { image_count: z.number(), images_size: z.number() },
      makeGenericZodErrorMessage('As imagens são obrigatórias!'),
    ),
  })
  .optional()

const schema = z.object({
  perimeter: perimeterSchema,
  mosaic: mosaicSchema,
  geotag: geoTagSchema,
  rgb: jpgSchema,
  thermal: jpgSchema,
})

export type DefaultSelectFilesFormData = z.infer<typeof schema>

export const useSelectFilesForm = (
  props?: UseFormProps<DefaultSelectFilesFormData>,
) => {
  const methods = useForm<DefaultSelectFilesFormData>({
    resolver: zodResolver(schema),
    ...props,
  })

  useValidationInputs(methods)

  return methods
}
